// Penalties register.
//
// Two audiences on one page, decided by the server: someone with the policy
// panel at Manage sees the whole branch and can levy and decide; everyone else
// sees only their own. That second case is the important one — being fined
// without being able to see the fine is not something worth shipping, so the
// page is reachable by default and simply shows an empty state when an org
// doesn't use penalties at all.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Gavel, Plus, Check, X, MinusCircle, Info, AlertTriangle, Loader2,
} from 'lucide-react';
import {
  decidePenalty,
  levyPenalty,
  listPenalties,
  type Penalty,
  type PenaltyList,
  type PenaltyStatus,
} from '../api/policyMoney';
import { getEmployees } from '../api/employees';

const STATUS_STYLE: Record<PenaltyStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-800 border-amber-200',
  APPROVED: 'bg-red-50 text-red-700 border-red-200',
  WAIVED: 'bg-gray-100 text-gray-600 border-gray-200',
  REJECTED: 'bg-gray-100 text-gray-500 border-gray-200',
};

// Only the rules that actually levy money. `penalty.compliance_tiers` derives
// its amount from a missed-day count, so it asks for one.
const PENALTY_RULES = [
  { key: 'penalty.asset_misuse', label: 'Asset misuse' },
  { key: 'penalty.asset_damage', label: 'Asset damage / loss' },
  { key: 'penalty.compliance_tiers', label: 'Missed daily updates' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function StatusPill({ status }: { status: PenaltyStatus }) {
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLE[status]}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function LevyModal({
  employees, defaultYear, defaultMonth, saving, error, onCancel, onSubmit,
}: {
  employees: Array<{ id: string; name: string }>;
  defaultYear: number;
  defaultMonth: number;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: any) => void;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [ruleKey, setRuleKey] = useState(PENALTY_RULES[0].key);
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [missedDays, setMissedDays] = useState('');
  const [description, setDescription] = useState('');
  const [override, setOverride] = useState('');

  const needsMissedDays = ruleKey === 'penalty.compliance_tiers';
  const input = 'border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-full bg-white';

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      role="dialog" aria-modal="true" aria-label="Levy a penalty">
      <div className="bg-white rounded-2xl border border-[#e8e3d8] w-full max-w-lg my-8">
        <div className="flex items-start justify-between p-4 border-b border-[#f0ece2]">
          <div>
            <h2 className="font-semibold text-gray-900">Levy a penalty</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Raised for review — nothing is deducted until it's approved.
            </p>
          </div>
          <button onClick={onCancel} aria-label="Close" className="p-1 text-gray-400 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-gray-600">Employee</span>
            <select className={input} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">Select an employee…</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-semibold text-gray-600">Reason</span>
            <select className={input} value={ruleKey} onChange={(e) => setRuleKey(e.target.value)}>
              {PENALTY_RULES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-xs font-semibold text-gray-600">Recover from</span>
              <select className={input} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold text-gray-600">Year</span>
              <input type="number" className={input} value={year}
                onChange={(e) => setYear(Number(e.target.value))} />
            </label>
          </div>

          {needsMissedDays && (
            <label className="block space-y-1">
              <span className="text-xs font-semibold text-gray-600">
                Days missed
                <span className="block font-normal text-[11px] text-gray-400">
                  The amount comes from the configured tier for this count.
                </span>
              </span>
              <input type="number" min={0} className={input} value={missedDays}
                onChange={(e) => setMissedDays(e.target.value)} />
            </label>
          )}

          <label className="block space-y-1">
            <span className="text-xs font-semibold text-gray-600">
              Note <span className="font-normal text-gray-400">(optional)</span>
            </span>
            <input className={input} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened" />
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-semibold text-gray-600">
              Override amount <span className="font-normal text-gray-400">(optional)</span>
              <span className="block font-normal text-[11px] text-gray-400">
                Leave blank to use the configured policy amount. An override is
                recorded as a manual figure.
              </span>
            </span>
            <input type="number" min={0} className={input} value={override}
              onChange={(e) => setOverride(e.target.value)} placeholder="Policy amount" />
          </label>

          {error && (
            <p className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg p-2.5">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#f0ece2]">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
          <button
            disabled={saving || !employeeId || (needsMissedDays && missedDays === '')}
            onClick={() => onSubmit({
              employee_id: employeeId,
              rule_key: ruleKey,
              year, month,
              ...(needsMissedDays ? { missed_days: Number(missedDays) } : {}),
              ...(description ? { description } : {}),
              ...(override !== '' ? { amount_override: Number(override) } : {}),
            })}
            className="bg-emerald-700 text-white rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-60"
          >
            {saving ? 'Raising…' : 'Raise penalty'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Penalties() {
  const now = new Date();
  const [data, setData] = useState<PenaltyList | null>(null);
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | ''>('');
  const [status, setStatus] = useState<PenaltyStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [levyOpen, setLevyOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const canManage = data?.scope === 'branch';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPenalties({
        year,
        ...(month ? { month: Number(month) } : {}),
        ...(status ? { status } : {}),
      });
      setData(res);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load penalties.');
    } finally {
      setLoading(false);
    }
  }, [year, month, status]);

  useEffect(() => { load(); }, [load]);

  // Only needed to populate the levy form; a failure here just leaves the
  // dropdown empty rather than breaking the page.
  useEffect(() => {
    if (!canManage) return;
    getEmployees()
      .then((res: any) => {
        const rows = Array.isArray(res) ? res : res?.data ?? [];
        setEmployees(rows.map((e: any) => ({
          id: e.id,
          name: [e.first_name, e.last_name].filter(Boolean).join(' ') || e.employeeID || e.id,
        })));
      })
      .catch(() => setEmployees([]));
  }, [canManage]);

  const nameFor = useMemo(
    () => (id: string) => employees.find((e) => e.id === id)?.name ?? id,
    [employees],
  );

  const decide = async (row: Penalty, next: 'APPROVED' | 'WAIVED' | 'REJECTED') => {
    setBusyId(row.id);
    try {
      await decidePenalty(row.id, next);
      setToast(
        next === 'APPROVED'
          ? `Approved — ${money(row.amount)} will be deducted from ${MONTHS[row.month - 1]} payroll.`
          : `Marked ${next.toLowerCase()}. Nothing will be deducted.`,
      );
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not update this penalty.');
    } finally {
      setBusyId(null);
    }
  };

  const submitLevy = async (body: any) => {
    setSaving(true);
    setSaveError(null);
    try {
      await levyPenalty(body);
      setLevyOpen(false);
      setToast('Penalty raised for review.');
      await load();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || 'Could not raise this penalty.');
    } finally {
      setSaving(false);
    }
  };

  const rows = data?.penalties ?? [];

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <Gavel className="h-6 w-6 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold">Penalties</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {canManage
                ? 'Fines raised across the organisation. Only approved penalties are deducted from payroll.'
                : 'Any penalties raised against you, and whether they were applied.'}
            </p>
          </div>
          {canManage && (
            <button
              onClick={() => { setSaveError(null); setLevyOpen(true); }}
              className="bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-sm font-medium inline-flex items-center gap-1.5 shrink-0"
            >
              <Plus size={15} /> Levy penalty
            </button>
          )}
        </div>

        {toast && (
          <div role="status" className="flex items-start justify-between gap-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-3">
            <p>{toast}</p>
            <button onClick={() => setToast(null)} aria-label="Dismiss" className="text-emerald-700">×</button>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-800 rounded-xl p-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Year" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select aria-label="Month" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={month} onChange={(e) => setMonth(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">All months</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select aria-label="Status" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={status} onChange={(e) => setStatus(e.target.value as PenaltyStatus | '')}>
            <option value="">All statuses</option>
            {(['PENDING', 'APPROVED', 'WAIVED', 'REJECTED'] as PenaltyStatus[]).map((s) => (
              <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
            ))}
          </select>
          {data && data.total_approved > 0 && (
            <span className="text-xs text-gray-500 ml-auto">
              Approved total: <strong className="text-gray-800">{money(data.total_approved)}</strong>
            </span>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        ) : rows.length === 0 ? (
          <div className="flex items-start gap-2 text-sm bg-white border border-[#e8e3d8] rounded-xl p-4 text-gray-600">
            <Info size={16} className="mt-0.5 shrink-0 text-gray-400" />
            <p>
              No penalties for this period.
              {canManage && ' Amounts come from Settings → Policy Rules, so configure those first.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#e8e3d8] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-[#f0ece2]">
                    {canManage && <th className="py-2 px-3 font-medium">Employee</th>}
                    <th className="py-2 px-3 font-medium">Reason</th>
                    <th className="py-2 px-3 font-medium">Recover from</th>
                    <th className="py-2 px-3 font-medium text-right">Amount</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    {canManage && <th className="py-2 px-3 font-medium text-right">Decide</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-[#f7f4ec] last:border-0">
                      {canManage && (
                        <td className="py-2 px-3 text-gray-900">{nameFor(row.emptID)}</td>
                      )}
                      <td className="py-2 px-3">
                        <div className="text-gray-900">
                          {PENALTY_RULES.find((r) => r.key === row.rule_key)?.label ?? row.rule_key}
                        </div>
                        {row.description && (
                          <div className="text-xs text-gray-500">{row.description}</div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                        {MONTHS[(row.month || 1) - 1]} {row.year}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-gray-900 whitespace-nowrap">
                        {money(row.amount)}
                      </td>
                      <td className="py-2 px-3">
                        <StatusPill status={row.status} />
                        {row.recovered_in_salary_id && (
                          <div className="text-[11px] text-gray-400 mt-0.5">Recovered</div>
                        )}
                      </td>
                      {canManage && (
                        <td className="py-2 px-3">
                          {row.status === 'PENDING' ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                disabled={busyId === row.id}
                                onClick={() => decide(row, 'APPROVED')}
                                aria-label={`Approve penalty for ${nameFor(row.emptID)}`}
                                title="Approve — this will be deducted"
                                className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                              >
                                <Check size={15} />
                              </button>
                              <button
                                disabled={busyId === row.id}
                                onClick={() => decide(row, 'WAIVED')}
                                aria-label={`Waive penalty for ${nameFor(row.emptID)}`}
                                title="Waive — keep the record, take no money"
                                className="p-1.5 text-gray-400 hover:text-amber-600 disabled:opacity-50"
                              >
                                <MinusCircle size={15} />
                              </button>
                              <button
                                disabled={busyId === row.id}
                                onClick={() => decide(row, 'REJECTED')}
                                aria-label={`Reject penalty for ${nameFor(row.emptID)}`}
                                title="Reject — shouldn't have been raised"
                                className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-50"
                              >
                                <X size={15} />
                              </button>
                            </div>
                          ) : (
                            <div className="text-right text-xs text-gray-400">
                              {row.decision_at ? 'Decided' : '—'}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {levyOpen && (
        <LevyModal
          employees={employees}
          defaultYear={year}
          defaultMonth={typeof month === 'number' ? month : now.getMonth() + 1}
          saving={saving}
          error={saveError}
          onCancel={() => setLevyOpen(false)}
          onSubmit={submitLevy}
        />
      )}
    </div>
  );
}
